import { Location } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { API_BASE } from '../api-base.token';
import { EquipmentReportResponse } from './equipment-report-response';
import { FireExtinguisherReportResponse } from './fire-extinguisher-report-response';
import { GroundCheckReportResponse } from './ground-check-response';
import { InspectionZoneReferences } from './inspection-zone-references';
import { InspectionZoneRequest } from './inspection-zone-request';
import { InspectionZoneResponse } from './inspection-zone-response';
import { MilesAndHoursResponse } from './miles-and-hours-response';
import { ZoneNotificationRequest } from './zone-notification-request';
import { ZoneNotificationResolutionRequest } from './zone-notification-resolution-request';
import { ZoneNotificationResponse } from './zone-notification-response';
import { ZoneStatusOverviewResponse } from './zone-status-overview-response';

const path = 'inspection-zones';

@Injectable({
  providedIn: 'root',
})
export class InspectionZonesHttpService {
  constructor(
    private readonly httpClient: HttpClient,
    @Inject(API_BASE)
    private readonly apiBase: string
  ) {}

  get() {
    const url = Location.joinWithSlash(this.apiBase, path);

    return this.httpClient.get<InspectionZoneResponse[]>(url);
  }

  getEquipmentReport(start: Date, end: Date, workspaceId?: string) {
    const params = [`start=${start.toISOString()}`, `end=${end.toISOString()}`];
    if (workspaceId) {
      params.push(`workspaceId=${workspaceId}`);
    }
    
    const url = Location.joinWithSlash(
      this.apiBase,
      `${path}/equipment-report?${params.join('&')}`
    );

    return this.httpClient.get<EquipmentReportResponse[]>(url);
  }

  getElectricalGroundingReport() {
    const url = Location.joinWithSlash(this.apiBase, `${path}/ground-report`);

    return this.httpClient.get<GroundCheckReportResponse>(url);
  }

  getFireExtinguisherReport(selectedMonth?: Date | null, timeZoneId?: string) {
    let url = Location.joinWithSlash(
      this.apiBase,
      `${path}/v2/fire-extinguisher-report`
    );

    // Append query parameters if provided
    const params: string[] = [];
    if (selectedMonth) {
      params.push(`selectedMonth=${selectedMonth.toISOString()}`);
    }
    if (timeZoneId) {
      params.push(`timeZoneId=${encodeURIComponent(timeZoneId)}`);
    }

    if (params.length > 0) {
      url += `?${params.join('&')}`;
    }

    return this.httpClient.get<FireExtinguisherReportResponse>(url);
  }

  getById(zoneId: string) {
    const url = Location.joinWithSlash(this.apiBase, `${path}/${zoneId}`);

    return this.httpClient.get<InspectionZoneResponse>(url);
  }

  getReferences(zoneId: string) {
    const url = Location.joinWithSlash(
      this.apiBase,
      `${path}/${zoneId}/references`
    );

    return this.httpClient.get<InspectionZoneReferences>(url);
  }

  getStatus(zoneId: string, inspectionDefinitionId: string) {
    const url = Location.joinWithSlash(
      this.apiBase,
      `${path}/${zoneId}/status/${inspectionDefinitionId}`
    );

    return this.httpClient.get<ZoneStatusOverviewResponse>(url);
  }

  post(inspectionZoneRequest: InspectionZoneRequest) {
    const url = Location.joinWithSlash(this.apiBase, path);

    return this.httpClient.post(url, inspectionZoneRequest);
  }

  put(zoneId: string, inspectionZoneRequest: InspectionZoneRequest) {
    const url = Location.joinWithSlash(this.apiBase, `${path}/${zoneId}`);

    return this.httpClient.put(url, inspectionZoneRequest);
  }

  deviceQrCodeExists(qrCode: string) {
    const url = Location.joinWithSlash(
      this.apiBase,
      `${path}/qr-code/${encodeURIComponent(qrCode)}/exists`
    );

    return this.httpClient.get<boolean>(url);
  }

  getNotifications(zoneId: string) {
    const url = Location.joinWithSlash(
      this.apiBase,
      `${path}/${zoneId}/notifications`
    );

    return this.httpClient.get<ZoneNotificationResponse[]>(url);
  }

  getMilesAndHours(zoneId: string) {
    const url = Location.joinWithSlash(
      this.apiBase,
      `${path}/${zoneId}/miles-and-hours`
    );

    return this.httpClient.get<MilesAndHoursResponse[]>(url);
  }

  addNotification(
    zoneId: string,
    notificationRequest: ZoneNotificationRequest
  ) {
    const url = Location.joinWithSlash(
      this.apiBase,
      `${path}/${zoneId}/notifications`
    );

    return this.httpClient.post(url, notificationRequest);
  }

  resolveNotification(
    zoneId: string,
    notificationId: string,
    request: ZoneNotificationResolutionRequest
  ) {
    const url = Location.joinWithSlash(
      this.apiBase,
      `${path}/${zoneId}/notifications/${notificationId}/resolve`
    );

    return this.httpClient.post(url, request);
  }

  lockOutZone(zoneId: string) {
    const url = Location.joinWithSlash(
      this.apiBase,
      `${path}/${zoneId}/lock-out`
    );

    return this.httpClient.post(url, {});
  }

  removeZoneLockOut(zoneId: string) {
    const url = Location.joinWithSlash(
      this.apiBase,
      `${path}/${zoneId}/un-lock`
    );

    return this.httpClient.post(url, {});
  }
}
